import React, { useEffect, useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { useUpdateTemplateContentMutation } from "./templatesApiSlice";
import toast from "../../../helpers/toast";

function TemplateCodeEditorModal({ open, onClose, template }) {
  const [content, setContent] = useState("");
  const [fetching, setFetching] = useState(false);
  const editorRef = useRef(null);

  const [updateTemplateContent, { isLoading: saving }] =
    useUpdateTemplateContentMutation();

  useEffect(() => {
    if (!open || !template?.file_url) return;

    setFetching(true);
    const url = `${template.file_url}?t=${Date.now()}`;
    fetch(url, { cache: "no-store" })
      .then((res) => res.text())
      .then((text) => setContent(text))
      .catch(() => {
        toast.error("Failed to load template content");
        setContent("");
      })
      .finally(() => setFetching(false));
  }, [open, template]);

  const handleSave = async () => {
    const currentContent = editorRef.current
      ? editorRef.current.getValue()
      : content;
    try {
      await updateTemplateContent({
        id: template.id,
        content: currentContent,
      }).unwrap();
      toast.success("Template content updated successfully");
      onClose();
    } catch (e) {
      toast.error(e?.data?.message || "Failed to save template content");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Edit Template Content — {template?.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {fetching ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={500}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Editor
            height="500px"
            defaultLanguage="html"
            value={content}
            onMount={(editor) => {
              editorRef.current = editor;
            }}
            options={{
              minimap: { enabled: false },
              fontSize: 13,
              wordWrap: "on",
              scrollBeyondLastLine: false,
              tabSize: 2,
            }}
          />
        )}
      </DialogContent>
      <Box
        display="flex"
        justifyContent="flex-end"
        px={3}
        py={1.5}
        borderTop="1px solid"
        borderColor="divider"
      >
        <StyledLoadingButton loading={saving} onClick={handleSave}>
          Save
        </StyledLoadingButton>
      </Box>
    </Dialog>
  );
}

export default TemplateCodeEditorModal;
